// =============================================================================
// ui/state/tabMemory.tsx — where a tab was, kept somewhere the tab cannot take
// with it when it dies.
// -----------------------------------------------------------------------------
// 🚨 THE DEFECT THIS EXISTS FOR WAS FILED FIVE TIMES AS FIVE BUGS. Review reset
// to the current week, Patterns landed back on Setups, Custom Analytics forgot
// your dashboard and period, the Journal lost the day you were on, and the
// Trades sort snapped back to date-descending. One cause; five audits, each
// looking at one tab, could not see it.
//
// ── ⚠ AND THE OBVIOUS CAUSE WAS THE WRONG ONE ──
//
// `AppShell` renders the tab inside `<div key={tabId}>`, which reads like the
// culprit: a changing key remounts a subtree. It is not. MEASURED with an
// isolated reproduction of that exact shape, with and without the key:
//
//     keyed=true  → came back showing "A:0"
//     keyed=false → came back showing "A:0"
//
// Identical. `App.tsx` returns a DIFFERENT COMPONENT TYPE per tab — twenty-one
// tabs, twenty-one types, none shared — so React unmounts the old type and
// mounts the new one on every switch regardless of any key. Deleting the key
// would have changed nothing, and that only becomes apparent after doing it.
//
// ▶ So the state cannot live in the tab. It has to live above it.
//
// ── ⚠ WHY NOT BETA'S ANSWER ──
//
// Beta keeps EVERY tab in the DOM and hides the inactive ones with CSS
// (`.tab-section { display:none }` / `.active { display:block }`), so nothing
// unmounts and nothing is forgotten. It can afford that: its whole book is
// already in memory. This client fetches per tab — twenty-one live tabs would
// mean twenty-one components holding subscriptions and refetching, which is a
// large ongoing cost to remember a sort order.
//
// ── 🔴 SESSION-SCOPED, AND THAT IS A DECISION ──
//
// This is "where I was a minute ago", not a preference. It deliberately does
// NOT reach `user_preferences` — that would be a network write on every click
// of a sort column — and it deliberately does NOT survive a reload, because
// beta's does not either. A reader who reloads expecting a clean slate and gets
// last week's Review has a different complaint, not a fixed one.
// =============================================================================

import {
  createContext, useCallback, useContext, useRef, useState,
  type ReactNode,
} from 'react';

/**
 * The store itself.
 *
 * ⚠ A `Map` IN A REF, not state. Putting it in state would re-render the
 * provider — and therefore the whole tab tree — every time anyone changed a sort
 * column. The map's identity never changes; the values inside it are read once,
 * when a tab mounts, and each consumer keeps its own `useState` after that.
 */
const TabMemoryContext = createContext<Map<string, unknown> | null>(null);

export function TabMemoryProvider({ children }: { children: ReactNode }) {
  const store = useRef<Map<string, unknown>>(new Map());
  return (
    <TabMemoryContext.Provider value={store.current}>
      {children}
    </TabMemoryContext.Provider>
  );
}

/**
 * `useState`, except the value outlives the component.
 *
 * Drop-in: same tuple, and the setter takes a value OR an updater function.
 *
 * ⚠ THE UPDATER FORM IS NOT OPTIONAL. `Trades` calls
 * `setSort((cur) => nextSort(cur, key))` — the next sort depends on the current
 * one, because clicking the active column flips its direction. A setter that
 * only took a value would have quietly broken that into "always descending".
 *
 * ⚠ `key` is a STRING NAMESPACE, not the tab id: one tab can remember several
 * things (Review keeps a cadence and an anchor). Namespace them `tab:thing`.
 *
 * ⚠ Outside a provider it degrades to plain `useState` rather than throwing.
 * A tab is reachable in tests and in isolated renders without the shell, and a
 * hook that explodes there would push every one of those to add a provider to
 * assert something unrelated. It simply forgets, which is the old behaviour.
 */
export function useTabMemory<T>(
  key: string, initial: T,
): [T, (next: T | ((cur: T) => T)) => void] {
  const store = useContext(TabMemoryContext);

  const [value, setValue] = useState<T>(
    () => (store?.has(key) ? store.get(key) as T : initial),
  );

  const set = useCallback((next: T | ((cur: T) => T)) => {
    setValue((cur) => {
      // ⚠ Resolved HERE rather than by the caller, so the value written to the
      // store is the one React actually committed. Writing `next` straight to
      // the map would store the function object when the updater form is used.
      const resolved = typeof next === 'function'
        ? (next as (c: T) => T)(cur)
        : next;
      store?.set(key, resolved);
      return resolved;
    });
  }, [store, key]);

  return [value, set];
}

/**
 * `useTabMemory`, plus a way to throw the slot away — for a DRAFT rather than a
 * view position.
 *
 * 🔴 WHY A SEPARATE HOOK AND NOT JUST "SET IT BACK TO THE SEED" (TCR-80). The
 * four record editors — trade, pattern, exit model, best opp — are modals with a
 * Cancel that discards. Autosaving them the way the Review tab now does would
 * make that button a lie, so they hold their draft here and write only on Save.
 * Cancel therefore has to CLEAR the slot, and the clear has to land before the
 * modal goes.
 *
 * ⚠ AND THAT IS EXACTLY WHY THE NORMAL SETTER WILL NOT DO. `set` writes to the
 * map from inside a `setValue` updater, so the write happens when React runs it
 * — and a handler that clears and then unmounts in the same tick may never get
 * there, leaving the discarded text in memory to reappear the next time the
 * record is opened. `discard` touches the `Map` in the ref directly, which has
 * already happened by the time the function returns.
 *
 * ⚠ IT IS NOT AN UNMOUNT CLEANUP, deliberately. Wiping the slot whenever the
 * component goes would throw the work away on navigation — the defect this is
 * here to fix. Only an explicit discard clears it.
 */
export function useTabMemoryDraft<T>(
  key: string, seed: T,
): [T, (next: T | ((cur: T) => T)) => void, () => void] {
  const store = useContext(TabMemoryContext);
  const [value, set] = useTabMemory<T>(key, seed);

  // ⚠ No state update here. The component is usually unmounting a line later,
  // and this must not depend on a render that may not come.
  const discard = useCallback(() => { store?.delete(key); }, [store, key]);

  return [value, set, discard];
}
